import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialogConfig } from '@angular/material/legacy-dialog';
import { AccountFacade } from '@mode/accounts/contract';
import { DatasourceFacade } from '@mode/accounts/domain';
import { BadgeComponent, IconComponent } from '@mode/capra';
import { DialogService } from '@mode/shared/ui';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { map, Observable, take } from 'rxjs';
import { DatasetSearchImportModalComponent } from '../dataset-search-import-modal/dataset-search-import-modal.component';
import { MetricsDialogComponent } from '../metrics-dialog/metrics-dialog.component';
import { DataTileComponent } from '../ui/data-tile/data-tile.component';

@Component({
  standalone: true,
  selector: 'mode-data-import-tiles',
  templateUrl: './data-import-tiles.component.html',
  styleUrls: ['./data-import-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, CommonModule, IconComponent, DataTileComponent],
})
export class DataImportTilesComponent implements OnInit {
  @Input() org?: string;

  queryMetricsEnabled$: Observable<boolean>;

  constructor(
    private dialogService: DialogService,
    private datasourceFacade: DatasourceFacade,
    private analyticsService: AnalyticsService,
    private accountFacade: AccountFacade,
    private location: Location
  ) {
    this.queryMetricsEnabled$ = this.accountFacade.currentOrg$.pipe(
      map((currentOrg) => currentOrg?.features.dbtMetrics)
    );
    this.datasourceFacade.loadDatasources();
  }

  ngOnInit(): void {
    this.queryMetricsEnabled$.pipe(take(1)).subscribe((metricsEnabled) => {
      if (!metricsEnabled) {
        this.selectDataset(true);
      }
    });
  }

  public navigateToLegacyNewReport() {
    this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.ReportEmptyState,
      action: 'query',
    });

    this.location.go(`/editor/${this.org}/reports/new`);
  }

  selectMetric(disableOpenAnimation = false) {
    this.sendTileAnalytics('dbt', 'MetricBrowser');

    const config: MatLegacyDialogConfig = {
      width: '80vw',
      minWidth: '500px',
      height: '70vh',
      minHeight: '400px',
    };

    if (disableOpenAnimation) {
      config.enterAnimationDuration = '0ms';
    }

    this.dialogService.open({
      component: MetricsDialogComponent,
      matDialogConfig: config,
    });
  }

  selectDataset(disableOpenAnimation = false) {
    this.sendTileAnalytics('datasets', 'DatasetBrowser', 'Report');

    const config: MatLegacyDialogConfig = {
      width: '80vw',
      minWidth: '500px',
      height: '70vh',
      minHeight: '400px',
      data: { org: this.org, newReport: true },
    };

    if (disableOpenAnimation) {
      config.enterAnimationDuration = '0ms';
    }

    this.dialogService.open({
      component: DatasetSearchImportModalComponent,
      matDialogConfig: config,
    });
  }

  private sendTileAnalytics(type: string, modalType?: string, context?: string) {
    this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.ModalShown, {
      location: AnalyticsEvent.SubContext.ReportEmptyState,
      action: type,
      modal_type: modalType,
      context: context,
    });
  }
}
